import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { normaliseAccount } from "@/lib/derivWs";

const pick = (accounts, type) => accounts.find((a) => a.account_type === type && a.status !== "inactive") || accounts.find((a) => a.account_type === type);

function selectAccount(sessionId, acct) {
  localStorage.setItem("naevo_active_account", acct.account_id);
  localStorage.setItem("naevo_loginid", acct.account_id);
  localStorage.setItem("naevo_currency", acct.currency || "USD");
  localStorage.setItem("naevo_account_type", acct.is_virtual ? "demo" : "real");
  return api.post("/account/select", { session_id: sessionId, account_id: acct.account_id }).catch(() => {});
}

export const DemoRealToggle = ({ sessionId, accounts, activeId }) => {
  const [busy, setBusy] = useState(false);
  const demo = pick(accounts, "demo");
  const real = pick(accounts, "real");
  const activeType = accounts.find((a) => a.account_id === activeId)?.account_type;

  const switchTo = async (acct) => {
    if (!acct || acct.account_id === activeId) return;
    setBusy(true);
    await selectAccount(sessionId, acct);
    toast.success(`Switched to ${acct.is_virtual ? "Demo" : "Real"} · ${acct.account_id}`);
    window.location.reload();
  };

  const createDemo = async () => {
    setBusy(true);
    try {
      const r = await api.post("/account/create-demo", { session_id: sessionId });
      const tok = r.data.token || localStorage.getItem("naevo_token");
      const list = (r.data.accounts || []).map((a) => normaliseAccount(a, tok));
      localStorage.setItem("naevo_accounts", JSON.stringify(list));
      const d = list.find((a) => a.account_id === r.data.account_id) || pick(list, "demo");
      if (!d) throw new Error("Demo account not returned");
      await selectAccount(sessionId, d);
      toast.success(`Demo account ready · ${d.account_id}`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not create demo account");
      setBusy(false);
    }
  };

  const Seg = ({ type, acct, label }) => (
    <button
      onClick={() => switchTo(acct)}
      disabled={busy || !acct}
      data-testid={`toggle-${type}`}
      className={`relative flex-1 rounded-full px-5 py-2.5 text-sm font-semibold tracking-wider transition-colors ${activeType === type ? (type === "demo" ? "bg-[#2AABEE] text-black" : "btn-gold") : "text-white/60 hover:text-white"} ${!acct ? "opacity-40" : ""}`}
    >
      {label}
      {acct?.balance != null && <span className="block text-[10px] font-mono-naevo opacity-80">{Number(acct.balance).toFixed(2)} {acct.currency}</span>}
    </button>
  );

  return (
    <div className="w-full sm:w-auto" data-testid="demo-real-toggle">
      <div className="flex items-center gap-1 rounded-full border border-gold/40 bg-black/60 p-1 min-w-[260px]">
        <Seg type="real" acct={real} label="REAL" />
        <Seg type="demo" acct={demo} label="DEMO" />
      </div>
      {demo && activeType === "demo" && (
        <button onClick={async () => {
          setBusy(true);
          try {
            await api.post("/account/reset-demo", { session_id: sessionId, account_id: demo.account_id });
            toast.success("Demo balance reset to $10,000");
            window.location.reload();
          } catch (e) { toast.error(e.response?.data?.detail || "Could not reset demo"); setBusy(false); }
        }} disabled={busy} data-testid="reset-demo-btn" className="mt-2 w-full rounded-full border border-[#2AABEE]/60 bg-[#2AABEE]/10 text-[#2AABEE] px-4 py-2 text-xs font-semibold tracking-wider flex items-center justify-center gap-2 hover:bg-[#2AABEE]/20">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} RESET DEMO TO $10,000
        </button>
      )}
      {!demo && (
        <button onClick={createDemo} disabled={busy} data-testid="create-demo-btn" className="mt-2 w-full rounded-full border border-[#2AABEE]/60 bg-[#2AABEE]/10 text-[#2AABEE] px-4 py-2 text-xs font-semibold tracking-wider flex items-center justify-center gap-2 hover:bg-[#2AABEE]/20">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} CREATE DEMO ACCOUNT · $10,000 PRACTICE
        </button>
      )}
    </div>
  );
};
